"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from "react";
import { usePathname } from "@/i18n/navigation";
import type { NavigationData } from "@/components/navigation/navigation.types";
interface NavigationState {
  isMobileMenuOpen: boolean;
  isSearchOpen: boolean;
}
type NavigationAction = {
  type: "OPEN_MOBILE_MENU";
} | {
  type: "CLOSE_MOBILE_MENU";
} | {
  type: "OPEN_SEARCH";
} | {
  type: "CLOSE_SEARCH";
} | {
  type: "RESET";
};
function navigationReducer(state: NavigationState, action: NavigationAction): NavigationState {
  switch (action.type) {
    case "OPEN_MOBILE_MENU":
      return {
        ...state,
        isMobileMenuOpen: true
      };
    case "CLOSE_MOBILE_MENU":
      return {
        ...state,
        isMobileMenuOpen: false
      };
    case "OPEN_SEARCH":
      return {
        ...state,
        isSearchOpen: true
      };
    case "CLOSE_SEARCH":
      return {
        ...state,
        isSearchOpen: false
      };
    case "RESET":
      return initialState;
  }
}
const initialState: NavigationState = {
  isMobileMenuOpen: false,
  isSearchOpen: false
};
interface NavigationContextValue {
  navigationData: NavigationData;
  isMobileMenuOpen: boolean;
  openMobileMenu: () => void;
  closeMobileMenu: () => void;
  isSearchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
}
const NavigationContext = createContext<NavigationContextValue | null>(null);
interface NavigationProviderProps {
  children: React.ReactNode;
  navigationData: NavigationData;
}
export function NavigationProvider(t0) {
  const $ = _c(14);
  const {
    children,
    navigationData
  } = t0;
  const pathname = usePathname();
  const [state, dispatch] = useReducer(navigationReducer, initialState);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => dispatch({
      type: "OPEN_MOBILE_MENU"
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const openMobileMenu = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => dispatch({
      type: "CLOSE_MOBILE_MENU"
    });
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const closeMobileMenu = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => dispatch({
      type: "OPEN_SEARCH"
    });
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const openSearch = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => dispatch({
      type: "CLOSE_SEARCH"
    });
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const closeSearch = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      dispatch({
        type: "RESET"
      });
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== pathname) {
    t6 = [pathname];
    $[5] = pathname;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  if ($[7] !== navigationData || $[8] !== state.isMobileMenuOpen || $[9] !== state.isSearchOpen) {
    t7 = {
      navigationData,
      isMobileMenuOpen: state.isMobileMenuOpen,
      openMobileMenu,
      closeMobileMenu,
      isSearchOpen: state.isSearchOpen,
      openSearch,
      closeSearch
    };
    $[7] = navigationData;
    $[8] = state.isMobileMenuOpen;
    $[9] = state.isSearchOpen;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const contextValue = t7;
  let t8;
  if ($[11] !== children || $[12] !== contextValue) {
    t8 = <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
    $[11] = children;
    $[12] = contextValue;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
export function useNavigationState() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigationState must be used within NavigationProvider");
  }
  return context;
}